// dayClose.js — the drawer count, and the control that checks it afterwards.
//
// Two surfaces, one file, because they are two halves of one discipline:
//
//   1. THE CEREMONY (cashier). Fold this device-day, show what the drawer SHOULD
//      hold, take the counted cash, show the variance, and record it with `close_day`.
//   2. THE CROSSCHECK (admin). `v_close_check` — what the device reported against
//      what the server folds — plus the two attribution controls no screen has ever
//      read: `v_attribution_mismatches` and `v_amount_line_mismatches`.
//
// ── WHICH ATTRIBUTION THIS SCREEN USES (spec §10.1, and it must say so) ──────
//
// The DAY CLOSE uses the TARGET SALE's device and business date for every correction.
// It answers « qu'aurait dû contenir CETTE caisse CE jour-là ? ». The dashboard tile
// and `v_daily_cash` answer a different question — « qu'a fait la position de
// trésorerie AUJOURD'HUI ? » — and book a correction on its OWN date. The two can show
// different numbers for the same day and both be right; every figure printed here is
// therefore labelled, so the next person to compare them does not "fix" one of them.
//
// ── THE BINDING GATE (spec §10.1, added 27 July 2026) ────────────────────────
//
// `foldDeviceDay` resolves a correction through its target sale. A device's local
// mirror can be incomplete — the correction pulled, its target not yet — and the fold
// then EXCLUDES that correction and reports it in `ignored[]` as
// `correction-target-not-in-set`.
//
// A device in that state reports a HIGHER `expected_cash` than the server holds, and
// produces a `v_close_check` delta that reads as a cash shortage for a cashier who is
// short nothing — the reconciliation control accusing an honest person. So:
//
//   **THE SUBMIT BUTTON IS REFUSED WHILE THAT CODE IS IN `ignored[]`.**
//
// That array is a GATE, not diagnostics. And the fold is handed the FULL event set,
// never a set pre-filtered to one device-day: such a set cannot contain the sales its
// corrections point at, so pre-filtering would manufacture the very condition the gate
// exists to catch.
//
// ── WHAT IS *NOT* A GATE ─────────────────────────────────────────────────────
//
// Unsynced PAYMENTS do not block a close. A sync backlog is a health indicator, not a
// lock: a cashier standing at a drawer at the end of a day must be able to count it
// and go home. The count is recorded as `unsynced_count` so `v_close_check` can read
// the delta against it later. `v_close_check` itself is a POST-HOC FLAG, never the
// gate (spec §10).

import { supabase } from './supabaseClient.js';
import { showStatus, formatCurrency } from './utils.js';

import { db } from './db/schema.js';
import { fetchAllPages, getAllEvents, pendingForDeviceDay } from './db/index.js';
import { getEngine } from './ui/engine-boot.js';
import { messageFor, messageForError, pendingInTotalNotice } from './ui/engine-messages.js';
import { businessDateOf, foldDeviceDay } from './money/index.js';
import { mergeConflicts, openConflicts } from './conflictQueue.js';

/**
 * Escape text destined for an `innerHTML` template (audit M7).
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** How far back an unclosed device-day stays reachable. */
export const UNCLOSED_SCAN_DAYS = 14;

/** The one `ignored[]` code that is a gate rather than a note (spec §10.1). */
export const GATE_IGNORE_CODE = 'correction-target-not-in-set';

// ---------------------------------------------------------------------------
// Pure logic — no DOM, no network. Unit-tested in dayClose.test.js.
// ---------------------------------------------------------------------------

/**
 * Shift a `YYYY-MM-DD` business date by whole days, in UTC so the answer never
 * depends on the machine's timezone (invariant 9: WAT is fixed, no `Intl`).
 * @param {string} dateStr
 * @param {number} days
 * @returns {string}
 */
export function shiftBusinessDate(dateStr, days) {
    const [y, m, d] = String(dateStr).split('-').map(Number);
    const shifted = new Date(Date.UTC(y, m - 1, d + days));
    const pad = (n, w) => String(n).padStart(w, '0');
    return `${pad(shifted.getUTCFullYear(), 4)}-${pad(shifted.getUTCMonth() + 1, 2)}-${pad(shifted.getUTCDate(), 2)}`;
}

/**
 * @typedef {Object} UnclosedDay
 * @property {string} businessDate `YYYY-MM-DD`.
 * @property {number} expectedCash Integer FCFA the drawer should have held.
 * @property {number} saleCount
 * @property {boolean} isToday
 */

/**
 * Every device-day in the window that has events and no close.
 *
 * WHY THIS EXISTS. Without it the control decays into "the days we happened to close":
 * a cashier who forgets a Friday has no route back to it, the day quietly never gets
 * counted, and `v_close_check` never gets a row to check. Fourteen days is long enough
 * to cover a holiday weekend plus a week of nobody noticing, and short enough that the
 * list stays a list rather than an archive.
 *
 * The fold is run over the FULL event set per candidate date (spec §10.1) — a set
 * pre-filtered to one device-day cannot contain the sales its corrections point at.
 *
 * @param {object[]} events The device's whole local mirror.
 * @param {Iterable<string>} closedDates Business dates already closed for this device.
 * @param {{deviceId:string, today:string, days?:number}} options
 * @returns {UnclosedDay[]} Oldest first — the order they should be worked in.
 */
export function scanUnclosedDays(events, closedDates, options) {
    const { deviceId, today, days = UNCLOSED_SCAN_DAYS } = options || {};
    if (!deviceId || !today) return [];

    const closed = new Set(closedDates || []);
    /** @type {UnclosedDay[]} */
    const out = [];

    for (let back = days; back >= 0; back -= 1) {
        const businessDate = shiftBusinessDate(today, -back);
        if (closed.has(businessDate)) continue;
        const fold = foldDeviceDay(events, { deviceId, businessDate });
        // A day with a sale fully cancelled by a void folds to zero cash but is still a
        // day that happened and still needs a close. Membership is "were there events",
        // never "was the total non-zero".
        if (fold.saleCount === 0 && fold.expectedCash === 0) continue;
        out.push({
            businessDate,
            expectedCash: fold.expectedCash,
            saleCount: fold.saleCount,
            isToday: businessDate === today
        });
    }
    return out;
}

/**
 * Which device-day a PARKED event belongs to — the client's mirror of
 * `private.event_drawer` (migration 0005).
 *
 * The gate here and the server's own gate must resolve identically, or the client
 * offers a close the server then refuses, or worse, hides a park the server will fold
 * into that day anyway.
 *
 * A parked void/discount takes its TARGET SALE's device and date (spec §10.1).
 * Everything else falls back to what the event itself claims — including the case that
 * makes `conflicts.device_id` a denormalized copy and not a foreign key: an event may
 * name a device that does not exist, and that can be the very reason it parked.
 *
 * `business_date` is server-derived and deliberately absent from the §6 wire payload,
 * so it is recovered from this device's ledger mirror (the mirror row is KEPT on a
 * park, precisely so the collected cash stays visible) and, failing that, derived from
 * `device_time`. When it cannot be derived at all the date is `null`, which the caller
 * must read as "counts against EVERY close for this device" — money whose day is
 * unknown must never be closed over silently.
 *
 * @param {{eventId:string, deviceId:string|null, event:object|null}} row A merged
 *   conflict row from `conflictQueue.js`.
 * @param {Map<string, object>} eventsById The local ledger mirror, keyed by id.
 * @returns {{deviceId:string|null, businessDate:string|null}}
 */
export function conflictDrawer(row, eventsById) {
    const event = (row && row.event) || {};
    const index = eventsById instanceof Map ? eventsById : new Map();

    if (event.kind === 'void' || event.kind === 'discount') {
        const target = index.get(event.reverses_id);
        if (target && target.kind === 'sale') {
            return {
                deviceId: target.device_id != null ? String(target.device_id) : null,
                businessDate: target.business_date != null ? String(target.business_date) : null
            };
        }
        // Target unknown to this device: the drawer cannot be named, so the park
        // blocks every close for whatever device the event does claim.
        return {
            deviceId: pickDevice(event, row),
            businessDate: null
        };
    }

    const mirror = row && row.eventId != null ? index.get(row.eventId) : null;
    let businessDate = mirror && mirror.business_date != null ? String(mirror.business_date) : null;
    if (businessDate == null && typeof event.device_time === 'string' && event.device_time !== '') {
        try {
            businessDate = businessDateOf(event.device_time);
        } catch {
            businessDate = null;
        }
    }
    return { deviceId: pickDevice(event, row), businessDate };
}

/**
 * @param {object} event
 * @param {{deviceId?:string|null}} row
 * @returns {string|null}
 */
function pickDevice(event, row) {
    if (event && event.device_id != null && event.device_id !== '') return String(event.device_id);
    if (row && row.deviceId != null && row.deviceId !== '') return String(row.deviceId);
    return null;
}

/**
 * The open conflicts that block one device-day's close.
 *
 * `close_day` refuses in exactly this case (migration 0005), so offering the button
 * would be offering an action guaranteed to fail. The client refuses first and names
 * the queue, which is an instruction; a server exception is not.
 *
 * @param {import('./conflictQueue.js').QueueRow[]} rows Merged conflict rows.
 * @param {Map<string, object>} eventsById Local ledger mirror by id.
 * @param {string} deviceId
 * @param {string} businessDate
 * @returns {import('./conflictQueue.js').QueueRow[]}
 */
export function blockingConflicts(rows, eventsById, deviceId, businessDate) {
    return openConflicts(rows).filter(row => {
        const drawer = conflictDrawer(row, eventsById);
        if (drawer.deviceId !== deviceId) return false;
        // A null date is conservative: it blocks EVERY close for this device, exactly
        // as `close_day`'s `coalesce(d.drawer_business_date, p_business_date)` does.
        return drawer.businessDate == null || drawer.businessDate === businessDate;
    });
}

/**
 * @typedef {Object} CloseBlocker
 * @property {string} code
 * @property {string} message French, and always an instruction.
 */

/**
 * May this device-day be submitted?
 *
 * THE ONLY THREE THINGS THAT REFUSE A CLOSE:
 *
 *   1. `correction-target-not-in-set` in `ignored[]` — the §10.1 binding gate. The
 *      device would report a higher `expected_cash` than the server holds and invent a
 *      shortage for a cashier who is short nothing. SYNC FIRST.
 *   2. An open conflict for this device-day — the server refuses too.
 *   3. The day is already closed, or this device has no identity.
 *
 * Everything else — a variance, a sync backlog, an `ignored[]` entry of any other kind
 * — is REPORTED and does not block. A cashier with a real variance must be able to
 * record it and go home; that variance IS the control.
 *
 * @param {{
 *   fold: {expectedCash:number, ignored:{reason:string}[]},
 *   blocking?: object[],
 *   alreadyClosed?: boolean,
 *   deviceId?: string|null
 * }} input
 * @returns {{allowed:boolean, blockers:CloseBlocker[], notes:CloseBlocker[]}}
 */
export function closeGate(input) {
    const { fold, blocking = [], alreadyClosed = false, deviceId = 'unknown' } = input || {};
    /** @type {CloseBlocker[]} */
    const blockers = [];
    /** @type {CloseBlocker[]} */
    const notes = [];

    if (!deviceId) {
        blockers.push({
            code: 'device-not-provisioned',
            message: messageFor('device-not-provisioned')
        });
    }

    const ignored = (fold && Array.isArray(fold.ignored)) ? fold.ignored : [];
    const orphans = ignored.filter(i => i && i.reason === GATE_IGNORE_CODE);
    if (orphans.length > 0) {
        blockers.push({
            code: GATE_IGNORE_CODE,
            message: `${orphans.length} correction(s) (annulation ou remise) désignent une vente que cet `
                + 'appareil ne connaît pas encore. Tant qu\'elles manquent, le montant attendu ci-dessus '
                + 'est TROP ÉLEVÉ et la clôture ferait apparaître un manquant qui n\'existe pas. '
                + 'Synchronisez cet appareil, puis rouvrez cette fenêtre.'
        });
    }

    // Every other ignored row is surfaced, never silently dropped (invariant 6) — and
    // never treated as a gate either. Half-counting is what lets two figures on one
    // screen disagree about the same row.
    const otherReasons = [...new Set(
        ignored.filter(i => i && i.reason !== GATE_IGNORE_CODE).map(i => i.reason)
    )];
    for (const reason of otherReasons) {
        notes.push({
            code: reason,
            message: `Écarté du calcul : ${messageFor(reason)}`
        });
    }

    if (Array.isArray(blocking) && blocking.length > 0) {
        blockers.push({
            code: 'open-conflict',
            message: `${blocking.length} enregistrement(s) de cette caisse ont été refusés par le serveur `
                + 'et attendent une décision. Le serveur refuse de clôturer une journée qui en contient : '
                + 'un administrateur doit d\'abord traiter la file des enregistrements refusés.'
        });
    }

    if (alreadyClosed) {
        blockers.push({
            code: 'already-closed',
            message: 'Cette journée est déjà clôturée pour ce poste. Une clôture ne se refait pas : '
                + 'les événements arrivés depuis sont visibles dans le contrôle de clôture (administrateur).'
        });
    }

    return { allowed: blockers.length === 0, blockers, notes };
}

/**
 * Read one `v_close_check` row.
 *
 * THE WHOLE POINT OF THIS FUNCTION is the distinction between a delta that is still
 * DRAINING and one that PERSISTS. Spec §10: a delta that persists once the backlog has
 * drained is the designed detection for cash credited on the wire that never entered a
 * drawer. A delta while the backlog is still arriving means nothing yet, and treating
 * the two alike either cries wolf every evening or hides the one row that matters.
 *
 * The rule, stated so it can be argued with:
 *
 *   * `delta === 0`                    → `clean`.
 *   * the device reported holding N unsent records at close, and FEWER than N events
 *     have reached the server for that drawer-day since → `draining`. Provisional; ask
 *     again once the queue empties.
 *   * otherwise (it claimed to hold nothing, or at least as many events have landed as
 *     it said it was holding) → `persistent`. THE RED ROW.
 *
 * `events_received_after_close` counts every event attributed to that drawer-day that
 * arrived after the close — the device's own backlog AND corrections authored later
 * (which by construction land after the close; see `v_close_check`'s own comment). It
 * is therefore a LOWER bound on how much of the backlog has drained, which makes
 * `draining` the conservative verdict and `persistent` the one that has to earn itself.
 *
 * @param {{delta:*, snapshot_unsynced_count:*, events_received_after_close:*}} row
 * @returns {{level:'clean'|'draining'|'persistent', label:string, detail:string}}
 */
export function classifyCloseCheck(row) {
    const delta = toInt(row && row.delta);
    const snapshot = toInt(row && row.snapshot_unsynced_count);
    const after = toInt(row && row.events_received_after_close);

    if (delta === 0) {
        return {
            level: 'clean',
            label: 'Concordant',
            detail: 'Le montant déclaré par le poste et le calcul du serveur coïncident.'
        };
    }

    if (snapshot > 0 && after < snapshot) {
        return {
            level: 'draining',
            label: 'En cours de synchronisation',
            detail: `Le poste déclarait ${snapshot} enregistrement(s) non encore envoyé(s) à la clôture ; `
                + `${after} événement(s) sont parvenus depuis. L'écart n'a pas encore de signification : `
                + 'attendez la fin de la synchronisation, puis relisez cette ligne.'
        };
    }

    return {
        level: 'persistent',
        label: 'Écart persistant',
        detail: snapshot === 0
            ? 'Le poste ne déclarait aucun enregistrement en attente à la clôture, et l\'écart demeure. '
                + 'À examiner : de l\'argent crédité au serveur qui n\'est jamais entré dans cette caisse '
                + 'se présente exactement ainsi.'
            : `Le poste déclarait ${snapshot} enregistrement(s) en attente ; ${after} événement(s) sont `
                + 'parvenus depuis, donc la file a drainé et l\'écart demeure. À examiner.'
    };
}

/**
 * Turn a `close_day` refusal into an instruction.
 *
 * `close_day` raises `55000` for the two states it refuses — an open conflict for the
 * device-day, and a day already closed — and `42501` for the role check. Rendering
 * those through the generic catalogue produces « Enregistrement refusé par le serveur
 * (code : 55000) », which names a Postgres errcode at a cashier standing in front of a
 * drawer. Each state has a different next step, so each gets its own sentence.
 *
 * @param {*} error
 * @returns {string}
 */
export function closeFailureMessage(error) {
    const code = error && error.code;
    const text = String((error && error.message) || '').toLowerCase();

    if (code === '42501') {
        return 'Votre compte n\'est pas autorisé à clôturer une caisse. '
            + 'Demandez à l\'administrateur de vérifier votre rôle.';
    }
    if (code === '55000' && text.includes('already closed')) {
        return 'Cette journée est déjà clôturée pour ce poste — une clôture ne se refait pas. '
            + 'Les événements arrivés depuis sont visibles dans le contrôle de clôture.';
    }
    if (code === '55000') {
        return 'Le serveur refuse : des enregistrements de cette caisse ont été refusés et attendent '
            + 'une décision. Un administrateur doit d\'abord traiter la file des enregistrements '
            + 'refusés, puis la clôture sera possible.';
    }
    return messageForError(error);
}

/** @param {*} v @returns {number} */
function toInt(v) {
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : 0;
}

/**
 * The variance a cashier is asked to explain: counted − expected.
 *
 * Positive = the drawer holds MORE than the ledger expects (excédent); negative = less
 * (manquant). Integer FCFA only — XAF has no subunit (invariant 5).
 *
 * @param {number} counted
 * @param {number} expected
 * @returns {number}
 */
export function varianceOf(counted, expected) {
    return toInt(counted) - toInt(expected);
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export const DayClose = {
    elements: {},
    /** @type {object[]} The device's full local mirror — the fold's input. */
    events: [],
    /** @type {Map<string, object>} */
    eventsById: new Map(),
    /** @type {import('./conflictQueue.js').QueueRow[]} */
    conflicts: [],
    /** @type {object[]} `day_closes` rows for this device. */
    closes: [],
    /** @type {object|null} The prepared close currently on screen. */
    prepared: null,
    /** @type {Map<string, string>} */
    deviceNames: new Map(),
    /** @type {Map<string, string>} */
    userEmails: new Map(),

    init() {
        this.elements = {
            modal: document.getElementById('day-close-modal'),
            closeModalBtn: document.getElementById('close-day-close-modal'),
            openBtns: [
                document.getElementById('day-close-btn'),
                document.getElementById('cashier-a-close-day-btn'),
                document.getElementById('cashier-b-close-day-btn')
            ].filter(Boolean),

            dateInput: document.getElementById('day-close-date'),
            reloadBtn: document.getElementById('day-close-reload'),
            unclosedList: document.getElementById('day-close-unclosed'),

            attribution: document.getElementById('day-close-attribution'),
            expected: document.getElementById('day-close-expected'),
            saleCount: document.getElementById('day-close-sale-count'),
            pendingNotice: document.getElementById('day-close-pending-notice'),
            blockers: document.getElementById('day-close-blockers'),
            notes: document.getElementById('day-close-notes'),

            form: document.getElementById('day-close-form'),
            counted: document.getElementById('day-close-counted'),
            variance: document.getElementById('day-close-variance'),
            varianceHint: document.getElementById('day-close-variance-hint'),
            note: document.getElementById('day-close-note'),
            submit: document.getElementById('day-close-submit'),

            adminSection: document.getElementById('day-close-admin'),
            checkBody: document.getElementById('close-check-body'),
            attributionBody: document.getElementById('attribution-mismatch-body'),
            amountBody: document.getElementById('amount-mismatch-body')
        };

        for (const btn of this.elements.openBtns) {
            btn.addEventListener('click', () => void this.openModal());
        }
        this.elements.closeModalBtn?.addEventListener('click', () => this.closeModal());
        this.elements.modal?.addEventListener('click', (e) => {
            if (e.target === this.elements.modal) this.closeModal();
        });
        this.elements.reloadBtn?.addEventListener('click', () => void this.prepare());
        this.elements.dateInput?.addEventListener('change', () => void this.prepare());
        this.elements.counted?.addEventListener('input', () => this.renderVariance());
        this.elements.form?.addEventListener('submit', (e) => void this.handleSubmit(e));

        this.elements.unclosedList?.addEventListener('click', (e) => {
            const button = e.target instanceof Element ? e.target.closest('button[data-date]') : null;
            if (!button) return;
            if (this.elements.dateInput) this.elements.dateInput.value = button.getAttribute('data-date');
            void this.prepare();
        });

        console.log('DayClose module initialized');
    },

    async openModal() {
        this.elements.modal?.classList.remove('hidden');
        if (this.elements.dateInput && !this.elements.dateInput.value) {
            this.elements.dateInput.value = this.today();
        }
        await this.prepare();
    },

    closeModal() {
        this.elements.modal?.classList.add('hidden');
    },

    /** Today's business date (WAT, fixed +01:00 — invariant 9). @returns {string} */
    today() {
        return businessDateOf(new Date().toISOString());
    },

    /** @returns {boolean} */
    isAdmin() {
        try {
            return !!(window.AppCore && window.AppCore.getCurrentRole
                && window.AppCore.getCurrentRole() === 'admin');
        } catch {
            return false;
        }
    },

    /** @returns {boolean} */
    isOnline() {
        return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    },

    // ---- preparing the close ----------------------------------------------

    /**
     * Fold this device-day and decide whether it may be submitted.
     *
     * THE FULL EVENT SET goes into `foldDeviceDay`, and the selector does the
     * filtering (spec §10.1). Handing it a pre-filtered set would remove the sales its
     * corrections point at and manufacture `correction-target-not-in-set` for every
     * correction on the day.
     */
    async prepare() {
        const engine = getEngine();
        const deviceId = engine ? engine.deviceId : null;
        const businessDate = (this.elements.dateInput && this.elements.dateInput.value)
            || this.today();

        // Sync first when we can: the §10.1 gate exists because a mirror can be
        // incomplete, and the cure for that is a pull, not a warning.
        if (engine && this.isOnline()) {
            try { await engine.kick(); } catch { /* the local fold below still stands */ }
        }

        this.events = await getAllEvents(db).catch(() => []);
        this.eventsById = new Map(this.events.filter(e => e && e.id != null).map(e => [e.id, e]));
        this.conflicts = await this.readConflicts();
        this.closes = await this.readCloses(deviceId);

        const fold = foldDeviceDay(this.events, { deviceId, businessDate });
        const blocking = deviceId
            ? blockingConflicts(this.conflicts, this.eventsById, deviceId, businessDate)
            : [];
        const alreadyClosed = this.closes.some(c => c && c.business_date === businessDate);
        const gate = closeGate({ fold, blocking, alreadyClosed, deviceId });

        let pending = { count: 0, total: 0 };
        if (deviceId) {
            pending = await pendingForDeviceDay(db, deviceId, businessDate).catch(() => ({ count: 0, total: 0 }));
        }

        this.prepared = { deviceId, businessDate, fold, gate, pending, blocking };
        this.render();

        if (this.isAdmin()) {
            if (this.elements.adminSection) this.elements.adminSection.classList.remove('hidden');
            await this.loadAdminControls();
        } else if (this.elements.adminSection) {
            this.elements.adminSection.classList.add('hidden');
        }
    },

    /** @returns {Promise<import('./conflictQueue.js').QueueRow[]>} */
    async readConflicts() {
        let localRows = [];
        try {
            localRows = await db.table('conflicts').toArray();
        } catch { /* an unreadable mirror must not stop a close from being prepared */ }

        let serverRows = [];
        const engine = getEngine();
        if (engine && engine.transport && this.isOnline()) {
            try {
                serverRows = await engine.transport.fetchConflicts({ status: 'open' });
            } catch { /* offline: the local mirror is what this device can prove */ }
        }
        return mergeConflicts(localRows, serverRows);
    },

    /**
     * This device's existing closes. Read from the server when we can, because the
     * close may have been made from another browser on the same device identity.
     * @param {string|null} deviceId
     * @returns {Promise<object[]>}
     */
    async readCloses(deviceId) {
        if (!deviceId || !this.isOnline()) return [];
        try {
            return await fetchAllPages((from, to) => supabase
                .from('day_closes')
                .select('id, device_id, business_date, expected_cash, counted_cash, variance, unsynced_count, note')
                .eq('device_id', deviceId)
                .order('business_date', { ascending: true })
                .order('id', { ascending: true })
                .range(from, to));
        } catch (error) {
            console.warn('Lecture des clôtures impossible:', error);
            return [];
        }
    },

    // ---- painting the ceremony --------------------------------------------

    render() {
        const p = this.prepared;
        if (!p) return;

        if (this.elements.attribution) {
            this.elements.attribution.textContent =
                'Ce montant répond à la question « qu\'aurait dû contenir CETTE caisse CE jour-là ? » : '
                + 'une annulation ou une remise est comptée sur la journée et le poste de la VENTE corrigée, '
                + 'pas sur la journée où elle a été saisie. Le tableau de bord répond à une autre question '
                + '(« qu\'a fait la trésorerie aujourd\'hui ? ») et peut donc afficher un autre chiffre pour '
                + 'la même date : les deux sont justes.';
        }

        if (this.elements.expected) {
            this.elements.expected.textContent = formatCurrency(p.fold.expectedCash);
        }
        if (this.elements.saleCount) {
            this.elements.saleCount.textContent = `${p.fold.saleCount} vente(s) comptée(s)`;
        }

        // Spec §9: queued offline money is IN the figure, and the caveat says how much.
        if (this.elements.pendingNotice) {
            const notice = pendingInTotalNotice(p.pending.count);
            this.elements.pendingNotice.textContent = notice
                ? `${notice} (${formatCurrency(p.pending.total)}). `
                    + 'Ce n\'est pas un obstacle à la clôture : le nombre est enregistré avec elle '
                    + 'pour que le contrôle sache lire l\'écart plus tard.'
                : '';
            this.elements.pendingNotice.classList.toggle('hidden', notice === '');
        }

        this.renderList(this.elements.blockers, p.gate.blockers, 'danger');
        this.renderList(this.elements.notes, p.gate.notes, 'muted');
        this.renderUnclosed();
        this.renderVariance();

        if (this.elements.submit) {
            this.elements.submit.disabled = !p.gate.allowed;
        }
        if (this.elements.counted) {
            this.elements.counted.disabled = !p.gate.allowed;
        }
    },

    /**
     * @param {HTMLElement|null} host
     * @param {CloseBlocker[]} items
     * @param {'danger'|'muted'} tone
     */
    renderList(host, items, tone) {
        if (!host) return;
        host.innerHTML = '';
        host.classList.toggle('hidden', !items || items.length === 0);
        for (const item of items || []) {
            const p = document.createElement('p');
            p.className = tone === 'danger' ? 'text-sm' : 'text-xs text-muted';
            if (tone === 'danger') p.style.color = 'var(--danger-600)';
            p.textContent = item.message;
            host.appendChild(p);
        }
    },

    renderUnclosed() {
        const host = this.elements.unclosedList;
        const p = this.prepared;
        if (!host || !p) return;

        host.innerHTML = '';
        if (!p.deviceId) {
            host.innerHTML = `<p class="text-sm text-muted">${escapeHtml(messageFor('device-not-provisioned'))}</p>`;
            return;
        }

        const closed = this.closes.map(c => c && c.business_date).filter(Boolean);
        const days = scanUnclosedDays(this.events, closed, {
            deviceId: p.deviceId,
            today: this.today()
        });
        const prior = days.filter(d => !d.isToday);

        if (!this.isOnline()) {
            // The list of existing closes lives on the server. Offline we cannot know
            // which days are already closed, so every day with events looks unclosed —
            // and saying so is the only honest thing to print.
            const warn = document.createElement('p');
            warn.className = 'text-xs text-muted';
            warn.textContent = 'Hors ligne : la liste des clôtures déjà faites ne peut pas être lue, '
                + 'des journées déjà clôturées peuvent donc apparaître ci-dessous.';
            host.appendChild(warn);
        }

        if (prior.length === 0) {
            const none = document.createElement('p');
            none.className = 'text-xs text-muted';
            none.textContent =
                `Aucune journée antérieure non clôturée sur les ${UNCLOSED_SCAN_DAYS} derniers jours.`;
            host.appendChild(none);
            return;
        }

        const intro = document.createElement('p');
        intro.className = 'text-sm';
        intro.style.color = 'var(--warning-700, #b45309)';
        intro.textContent = `${prior.length} journée(s) antérieure(s) de ce poste n'ont pas été clôturées. `
            + 'Une journée qu\'on ne peut plus atteindre est une journée qui ne sera jamais comptée :';
        host.appendChild(intro);

        for (const day of prior) {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'btn btn-secondary btn-sm';
            button.style.margin = '0.15rem';
            button.textContent = `${day.businessDate} — ${formatCurrency(day.expectedCash)} (${day.saleCount} vente(s))`;
            button.setAttribute('data-date', day.businessDate);
            host.appendChild(button);
        }
    },

    /**
     * The variance, prominently, with the §9 caveat naming how many unsynced sales are
     * inside the figure it is measured against.
     */
    renderVariance() {
        const p = this.prepared;
        if (!p || !this.elements.variance) return;

        const raw = this.elements.counted ? this.elements.counted.value : '';
        const counted = parseCounted(raw);

        if (counted == null) {
            this.elements.variance.textContent = '—';
            this.elements.variance.style.color = '';
            if (this.elements.varianceHint) {
                this.elements.varianceHint.textContent =
                    'Saisissez le montant réellement compté dans la caisse, en FCFA entiers.';
            }
            return;
        }

        const variance = varianceOf(counted, p.fold.expectedCash);
        this.elements.variance.textContent = `${variance > 0 ? '+' : ''}${formatCurrency(variance)}`;
        this.elements.variance.style.color = variance === 0
            ? 'var(--success-600, #16a34a)'
            : 'var(--danger-600)';

        if (this.elements.varianceHint) {
            const caveat = p.pending.count > 0
                ? ` ${pendingInTotalNotice(p.pending.count)} — l'écart est mesuré contre un montant attendu `
                    + 'qui les contient déjà.'
                : '';
            this.elements.varianceHint.textContent = (variance === 0
                ? 'La caisse correspond exactement au montant attendu.'
                : variance > 0
                    ? 'Excédent : la caisse contient plus que le registre n\'attend. Expliquez-le dans la note.'
                    : 'Manquant : la caisse contient moins que le registre n\'attend. Expliquez-le dans la note.')
                + caveat;
        }
    },

    // ---- submitting --------------------------------------------------------

    /**
     * @param {Event} e
     */
    async handleSubmit(e) {
        e.preventDefault();
        const p = this.prepared;
        if (!p) return;

        // Re-checked here and not only in the disabled button: a stale DOM state must
        // never be the only thing standing between a device with an incomplete mirror
        // and a `v_close_check` delta that accuses an honest cashier (spec §10.1).
        if (!p.gate.allowed) {
            showStatus(p.gate.blockers[0]?.message || 'Clôture impossible.', true);
            return;
        }

        const counted = parseCounted(this.elements.counted ? this.elements.counted.value : '');
        if (counted == null) {
            showStatus('Saisissez le montant compté en FCFA entiers (chiffres uniquement).', true);
            return;
        }

        const engine = getEngine();
        if (!engine || !engine.transport) {
            showStatus(messageFor('engine-not-ready'), true);
            return;
        }
        if (!this.isOnline()) {
            showStatus('La clôture exige une connexion au serveur. Rétablissez le réseau, puis recommencez. '
                + 'Les ventes déjà enregistrées ne sont pas perdues.', true);
            return;
        }

        const variance = varianceOf(counted, p.fold.expectedCash);
        const note = (this.elements.note && this.elements.note.value.trim()) || null;
        if (variance !== 0 && !note) {
            showStatus('Un écart doit être expliqué : saisissez une note avant de clôturer.', true);
            return;
        }

        if (!confirm(
            `Clôturer la journée ${p.businessDate} pour ce poste ?\n\n`
            + `Attendu : ${formatCurrency(p.fold.expectedCash)}\n`
            + `Compté  : ${formatCurrency(counted)}\n`
            + `Écart   : ${variance > 0 ? '+' : ''}${formatCurrency(variance)}\n\n`
            + 'Une clôture ne se refait pas.')) {
            return;
        }

        // Snapshot the backlog AT THE MOMENT OF THE CLOSE, not at prepare time: it is
        // the figure `v_close_check` reads to tell a draining delta from a persistent
        // one, and a stale count makes that distinction lie.
        const pending = await pendingForDeviceDay(db, p.deviceId, p.businessDate)
            .catch(() => p.pending);

        if (this.elements.submit) this.elements.submit.disabled = true;
        try {
            showStatus('Clôture en cours…', false);
            await engine.transport.closeDay({
                deviceId: p.deviceId,
                businessDate: p.businessDate,
                expectedCash: p.fold.expectedCash,
                countedCash: counted,
                unsyncedCount: pending.count,
                note
            });
            if (this.elements.counted) this.elements.counted.value = '';
            if (this.elements.note) this.elements.note.value = '';
            showStatus(`Journée ${p.businessDate} clôturée. `
                + `Écart : ${variance > 0 ? '+' : ''}${formatCurrency(variance)}.`, false);
            await this.prepare();
        } catch (error) {
            console.error('Clôture refusée:', error);
            showStatus(closeFailureMessage(error), true);
            await this.prepare();
        } finally {
            if (this.elements.submit) this.elements.submit.disabled = !this.prepared?.gate.allowed;
        }
    },

    // ---- the admin controls ------------------------------------------------

    /**
     * `v_close_check` plus the two mismatch views no screen has ever read.
     *
     * All three are POST-HOC controls. None of them gates anything, and none of them is
     * an error: `v_attribution_mismatches` is NORMAL in the handoff case (one cashier
     * takes the cash, another pushes it), which is exactly why it needs a reviewable
     * line rather than a silent pass.
     */
    async loadAdminControls() {
        const engine = getEngine();
        if (!engine || !engine.transport || !this.isOnline()) {
            this.setTableMessage(this.elements.checkBody, 7,
                'Contrôles indisponibles hors ligne — ils se lisent sur le serveur.');
            this.setTableMessage(this.elements.attributionBody, 6, '');
            this.setTableMessage(this.elements.amountBody, 6, '');
            return;
        }

        await Promise.all([this.loadDeviceNames(), this.loadUserEmails()]);

        try {
            const [checks, attribution, amounts] = await Promise.all([
                engine.transport.fetchView({ view: 'v_close_check', orderBy: ['business_date', 'close_id'] }),
                engine.transport.fetchView({ view: 'v_attribution_mismatches', orderBy: ['business_date', 'id'] }),
                engine.transport.fetchView({ view: 'v_amount_line_mismatches', orderBy: ['business_date', 'event_id'] })
            ]);
            this.renderCloseChecks(checks);
            this.renderAttributionMismatches(attribution);
            this.renderAmountMismatches(amounts);
        } catch (error) {
            console.error('Lecture des contrôles impossible:', error);
            // A partial fetch REFUSES, it never renders (spec §8, audit C6).
            const message = `Contrôles non affichés : ${messageForError(error)}`;
            this.setTableMessage(this.elements.checkBody, 7, message);
            this.setTableMessage(this.elements.attributionBody, 6, message);
            this.setTableMessage(this.elements.amountBody, 6, message);
        }
    },

    async loadDeviceNames() {
        try {
            const rows = await fetchAllPages((from, to) => supabase
                .from('devices').select('id, name')
                .order('id', { ascending: true }).range(from, to));
            this.deviceNames = new Map(rows.map(d => [d.id, d.name]));
        } catch { /* a bare id is worse than a name, but it is not wrong */ }
    },

    async loadUserEmails() {
        try {
            const rows = await fetchAllPages((from, to) => supabase
                .from('users').select('id, email')
                .order('id', { ascending: true }).range(from, to));
            this.userEmails = new Map(rows.map(u => [u.id, u.email]));
        } catch { /* audit L7: never a join, and never a throw from a missing name */ }
    },

    /** @param {*} id @returns {string} */
    deviceLabel(id) {
        if (!id) return 'poste inconnu';
        return this.deviceNames.get(id) || `poste inconnu (${String(id).slice(0, 8)}…)`;
    },

    /** @param {*} id @returns {string} */
    userLabel(id) {
        if (!id) return 'compte inconnu';
        return this.userEmails.get(id) || `compte inconnu (${String(id).slice(0, 8)}…)`;
    },

    /**
     * @param {HTMLElement|null} body
     * @param {number} cols
     * @param {string} message
     */
    setTableMessage(body, cols, message) {
        if (!body) return;
        body.innerHTML = `<tr><td colspan="${cols}" class="text-center text-muted">${escapeHtml(message)}</td></tr>`;
    },

    /** @param {object[]} rows */
    renderCloseChecks(rows) {
        const body = this.elements.checkBody;
        if (!body) return;
        if (!rows || rows.length === 0) {
            this.setTableMessage(body, 7, 'Aucune clôture enregistrée.');
            return;
        }

        body.innerHTML = '';
        // Newest first: the row an admin is looking for is almost always yesterday's.
        const sorted = [...rows].sort((a, b) =>
            String(b.business_date || '').localeCompare(String(a.business_date || '')));

        for (const row of sorted) {
            const verdict = classifyCloseCheck(row);
            const badge = verdict.level === 'persistent'
                ? '<span class="badge badge-danger">Écart persistant</span>'
                : verdict.level === 'draining'
                    ? '<span class="badge badge-warning">En cours de synchronisation</span>'
                    : '<span class="badge badge-success">Concordant</span>';

            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td class="font-medium">${escapeHtml(row.business_date)}</td>
                <td class="text-sm">${escapeHtml(this.deviceLabel(row.device_id))}</td>
                <td>${escapeHtml(formatCurrency(toInt(row.expected_cash)))}</td>
                <td>${escapeHtml(formatCurrency(toInt(row.server_fold)))}</td>
                <td class="font-medium">${escapeHtml(formatCurrency(toInt(row.delta)))}</td>
                <td class="text-sm">${escapeHtml(String(toInt(row.events_received_after_close)))}
                    <span class="text-xs text-muted">(file déclarée : ${escapeHtml(String(toInt(row.snapshot_unsynced_count)))})</span></td>
                <td class="text-sm">${badge}<div class="text-xs text-muted">${escapeHtml(verdict.detail)}</div></td>`;
            body.appendChild(tr);
        }
    },

    /** @param {object[]} rows */
    renderAttributionMismatches(rows) {
        const body = this.elements.attributionBody;
        if (!body) return;
        if (!rows || rows.length === 0) {
            this.setTableMessage(body, 6, 'Aucun écart d\'attribution : encaisseur et enregistreur coïncident partout.');
            return;
        }
        body.innerHTML = '';
        for (const row of rows) {
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td>${escapeHtml(row.business_date)}</td>
                <td>${escapeHtml(row.kind)}</td>
                <td>${escapeHtml(formatCurrency(toInt(row.amount)))}</td>
                <td class="text-sm">${escapeHtml(this.userLabel(row.collected_by))}</td>
                <td class="text-sm">${escapeHtml(this.userLabel(row.recorded_by))}</td>
                <td class="text-sm">${row.receipt_no != null
                    ? `<code>${escapeHtml(String(row.receipt_no))}</code>` : '—'}
                    ${row.patient_name ? `<div class="text-xs text-muted">${escapeHtml(row.patient_name)}</div>` : ''}</td>`;
            body.appendChild(tr);
        }
    },

    /** @param {object[]} rows */
    renderAmountMismatches(rows) {
        const body = this.elements.amountBody;
        if (!body) return;
        if (!rows || rows.length === 0) {
            this.setTableMessage(body, 6, 'Aucune vente dont les lignes ne totalisent pas son montant.');
            return;
        }
        body.innerHTML = '';
        for (const row of rows) {
            const tr = document.createElement('tr');
            tr.innerHTML = `
                <td>${escapeHtml(row.business_date)}</td>
                <td class="text-sm">${escapeHtml(this.deviceLabel(row.device_id))}</td>
                <td>${row.receipt_no != null ? `<code>${escapeHtml(String(row.receipt_no))}</code>` : '—'}</td>
                <td>${escapeHtml(formatCurrency(toInt(row.recorded_amount)))}</td>
                <td>${escapeHtml(formatCurrency(toInt(row.line_sum)))}</td>
                <td class="font-medium">${escapeHtml(formatCurrency(toInt(row.delta)))}</td>`;
            body.appendChild(tr);
        }
    }
};

/**
 * Parse a counted-cash input.
 *
 * Integer FCFA only (invariant 5): `parseFloat` NaN must never slip past a `<= 0`
 * guard, and a decimal must never be silently truncated. Spaces are tolerated because
 * a cashier types « 42 500 ».
 *
 * @param {string} raw
 * @returns {number|null} `null` when the value is not an integer amount.
 */
export function parseCounted(raw) {
    // U+00A0 and U+202F are what Intl.NumberFormat('fr-CM') puts between the thousands,
    // so a cashier who copies a figure off this very screen back into the field is not
    // told that their own number is not a number.
    const text = String(raw == null ? '' : raw).replace(/[\s  ]/g, '');
    if (text === '' || !/^\d{1,12}$/.test(text)) return null;
    const n = Number(text);
    return Number.isSafeInteger(n) ? n : null;
}

// Guarded, because the PURE half of this module (the unclosed-days scan, the close
// gate, the drained-vs-draining classification) is unit-tested in Node, where there
// is no `window`. An unguarded assignment would throw at import and take the tests
// for the money rules down with it.
if (typeof window !== 'undefined') window.DayClose = DayClose;
